/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import PageSkeleton from '../../components/common/page-skeleton';
import { Button } from 'sharedUi/Button';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { formatDate as formatDateShared } from 'sharedUi/dateUtils';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { unitsService } from '../../services/units.service';
import { api } from '../../services/api';
import type { Unit } from '../../types';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { extractErrorMessage } from '../../utils/error-handler';
import { usePersonnelNames } from '../../hooks/usePersonnelLookup';
import { useCanUpdate, useCanDelete } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import EntityLink from '../../components/common/entity-link';
import { AuditInfoCard } from '../../components/common/audit-info-card';
import ScreenHeader from '../../components/common/screen-header';

const FILE_UPLOAD_API = import.meta.env.VITE_FILE_UPLOAD_API || 'https://devapi.ai4andhrapolice.com/core-fileupload/api/v1';

/* ---------------------------------------------------------------------- */
/* Main UnitView Component */
/* ---------------------------------------------------------------------- */
const UnitView: React.FC = () => {
  const navigate = useAppNavigate();
  const { id, isReady, navigateToEdit, navigateToList } = useSecureNavigation({
    entity: 'units',
    basePath: '/units',
  });

  const canUpdate = useCanUpdate(JOB_NAMES.UNITS);
  const canDelete = useCanDelete(JOB_NAMES.UNITS);

  const [unit, setUnit] = useState<Unit | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [logoLoading, setLogoLoading] = useState<boolean>(false);

  // Set breadcrumb title to show unit name instead of UUID
  useBreadcrumbTitle(unit?.name);

  // Collect all personnel IDs for lookup
  const personnelIds = React.useMemo(() => {
    if (!unit) return [];
    const unitAny = unit as any;
    const ids: (string | null | undefined)[] = [
      unit.responsibleUserId,
      unit.proxyUserId,
      unit.createdBy,
      unit.updatedBy,
      ...(unit.unitPersonnelList || []),
      ...(unitAny.responsibleUserHistory?.map((h: any) => h.userId) || []),
      ...(unitAny.responsibleUserHistory?.map((h: any) => h.changedBy) || []),
      // Include assigned users from embedded posts (if they are IDs, not populated objects)
      ...(unit.posts || [])
        .filter((p: any) => p.assignedUser && typeof p.assignedUser === 'string')
        .map((p: any) => p.assignedUser),
    ];
    return ids;
  }, [unit]);

  // Use cached personnel lookup for names
  const { getName: getPersonnelName } = usePersonnelNames(personnelIds);

  // Unit id -> name map, so hierarchy-path entries never fall back to a raw ObjectId.
  const [unitNameMap, setUnitNameMap] = useState<Record<string, string>>({});
  useEffect(() => {
    let mounted = true;
    unitsService
      .getMinimalTree()
      .then((list) => {
        if (!mounted) return;
        const map: Record<string, string> = {};
        list.forEach((u) => { if (u._id) map[u._id] = u.name; });
        setUnitNameMap(map);
      })
      .catch(() => { /* names simply won't resolve; harmless */ });
    return () => { mounted = false; };
  }, []);
  // Resolve a path entry to the best available label (never a bare id).
  const isObjectId = (s?: string) => !!s && /^[a-f0-9]{24}$/i.test(s);
  const labelFor = (entry: { _id?: string; name?: string }) =>
    entry.name || (entry._id ? unitNameMap[entry._id] : undefined) || (isObjectId(entry.name) ? '' : entry.name) || entry._id || '-';

  const formatDate = (dateStr?: string | null) => formatDateShared(dateStr);
  // Fetch logo URL from file service
  const fetchLogoUrl = async (logoId: string): Promise<string | null> => {
    try {
      setLogoLoading(true);
      const response = await api.get(`${FILE_UPLOAD_API}/files/download/${logoId}?inline=true`);
      if (response.data?.success && response.data?.data?.url) {
        return response.data.data.url;
      }
      return null;
    } catch (err) {
      console.error('Failed to fetch logo URL:', err);
      return null;
    } finally {
      setLogoLoading(false);
    }
  };

  // Fetch unit details
  // Only fetch when isReady is true (decryption complete)
  useEffect(() => {
    if (!isReady || !id) {
      // ID is still decrypting or not available - wait
      return;
    }

    let mounted = true;

    const loadUnit = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await unitsService.getById(id);
        if (mounted) {
          setUnit(data);
          // Fetch logo URL if logo exists
          if (data.logo) {
            const url = await fetchLogoUrl(data.logo);
            if (mounted && url) {
              setLogoUrl(url);
            }
          }
        }
      } catch (err: any) {
        if (mounted) {
          setError(extractErrorMessage(err, 'Failed to fetch unit details'));
          setUnit(null);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadUnit();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, id]);

  // Handlers
  const handleEdit = () => id && navigateToEdit(id);
  const handleDeleteClick = () => setDeleteDialogOpen(true);

  const handleDeleteConfirm = async () => {
    if (!id) return;
    try {
      await unitsService.delete(id);
      setDeleteDialogOpen(false);
      navigateToList();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to delete unit'));
    }
  };

  const handleRestoreConfirm = async () => {
    if (!id) return;
    try {
      await unitsService.restore(id);
      const data = await unitsService.getById(id);
      setUnit(data);
      setRestoreDialogOpen(false);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to restore unit'));
    }
  };

  const handleLogoClick = () => {
    if (logoUrl) {
      setImagePreviewOpen(true);
    }
  };

  // Loading state
  if (loading) {
    return (
      <PageSkeleton />
    );
  }

  // Error state
  if (error || !unit) {
    return (
      <div className="p-4">
        <Message severity="error" text={error || 'Unit not found'} className="mb-4 w-full" />
        <Button
          label="Back to Units"
          icon="pi pi-arrow-left"
          severity="secondary"
          outlined
          onClick={() => navigateToList()}
        />
      </div>
    );
  }

  const deleteDialogFooter = (
    <div className="flex justify-end gap-2">
      <Button label="Cancel" severity="secondary" outlined onClick={() => setDeleteDialogOpen(false)} />
      <Button label="Delete" severity="danger" onClick={handleDeleteConfirm} />
    </div>
  );

  const restoreDialogFooter = (
    <div className="flex justify-end gap-2">
      <Button label="Cancel" severity="secondary" outlined onClick={() => setRestoreDialogOpen(false)} />
      <Button label="Restore" severity="success" onClick={handleRestoreConfirm} />
    </div>
  );

  return (
    <div className="p-3" data-testid="SCR-Unit-View">
      {/* Header Section */}
      <ScreenHeader icon="pi pi-building" title="Unit Details" onBack={() => navigateToList()} />

      {/* Unit Header with Logo */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-2">
        <div className="flex items-start gap-2.5">
          {/* Logo */}
          {logoLoading ? (
            <div className="w-12 h-12 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
              <ProgressSpinner style={{ width: '20px', height: '20px' }} />
            </div>
          ) : logoUrl ? (
            <div
              className="w-12 h-12 rounded border border-gray-200 dark:border-gray-600 overflow-hidden bg-white cursor-pointer flex-shrink-0"
              onClick={handleLogoClick}
            >
              <img src={logoUrl} alt={unit.name} className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="w-12 h-12 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
              <span className="text-lg font-semibold text-gray-400">{unit.name?.charAt(0) || '?'}</span>
            </div>
          )}

          <div>
            <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
              <h2 className="text-base font-semibold text-ink">{unit.name}</h2>
              {unit.unitCode && (
                <span className="text-xs font-medium text-ink-muted bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                  {unit.unitCode}
                </span>
              )}
              {unit.isDeleted ? (
                <Tag value="Deleted" severity="danger" className="text-xs px-1.5 py-0.5" />
              ) : (
                <Tag value="Active" severity="success" className="text-xs px-1.5 py-0.5" />
              )}
              {unit.isVirtual && <Tag value="Virtual" severity="info" className="text-xs px-1.5 py-0.5" />}
            </div>
            <p className="text-xs text-ink-muted">
              {unit.unitType?.name || '-'}
            </p>
          </div>
        </div>

        {/* Edit/Delete buttons aligned with unit name row */}
        {!((unit as any).isDelete || unit.isDeleted) && (
          <div className="flex gap-1.5 sm:self-center">
            {canUpdate && (
              <Button
                label="Edit"
                icon="pi pi-pencil"
                onClick={handleEdit}
                size="small"
              />
            )}
            {canDelete && (
              <Button
                label="Delete"
                icon="pi pi-trash"
                severity="danger"
                onClick={handleDeleteClick}
                size="small"
              />
            )}
          </div>
        )}
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
          {/* Basic Information */}
          <div className="ds-card p-3">
            <h2 className="text-sm font-semibold text-ink mb-2">Basic Information</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
              <div>
                <label className="text-xs text-ink-muted block mb-0.5">Unit Name</label>
                <p className="text-sm text-ink">{unit.name || '-'}</p>
              </div>
              <div>
                <label className="text-xs text-ink-muted block mb-0.5">Unit Code</label>
                <p className="text-sm text-ink">{unit.unitCode || '-'}</p>
              </div>
              <div>
                <label className="text-xs text-ink-muted block mb-0.5">Department</label>
                <EntityLink type="department" id={unit.departmentId} label={unit.department?.name} />
              </div>
              <div>
                <label className="text-xs text-ink-muted block mb-0.5">Unit Type</label>
                <EntityLink type="unitType" id={unit.unitTypeId} label={unit.unitType?.name} />
              </div>
              <div>
                <label className="text-xs text-ink-muted block mb-0.5">Parent Unit</label>
                <EntityLink type="unit" id={unit.parentUnitId ?? undefined} label={unit.parentUnit?.name} fallback="None" />
              </div>
              <div>
                <label className="text-xs text-ink-muted block mb-0.5">Virtual Unit</label>
                <p className="text-sm text-ink">{unit.isVirtual ? 'Yes' : 'No'}</p>
              </div>
              <div>
                <label className="text-xs text-ink-muted block mb-0.5">Responsible User</label>
                <p className="text-sm text-ink">
                  {unit.responsibleUserName || (unit.responsibleUserId ? getPersonnelName(unit.responsibleUserId) : '-')}
                </p>
              </div>
              {unit.responsiblePersonTitle && (
                <div>
                  <label className="text-xs text-ink-muted block mb-0.5">Responsible Person Title</label>
                  <p className="text-sm text-ink">{unit.responsiblePersonTitle}</p>
                </div>
              )}
            </div>
          </div>

          {/* District (address/email/phone are not stored for these units) */}
          <div className="ds-card p-3">
            <h2 className="text-sm font-semibold text-ink mb-2">Location</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
              <div>
                <label className="text-xs text-ink-muted block mb-0.5">District</label>
                <EntityLink type="district" id={unit.districtId} label={unit.district?.name} />
              </div>
            </div>
          </div>

          {/* Responsible Personnel - Commented out per request */}
          {/* <div className="ds-card p-5">
            <h2 className="text-base font-semibold text-ink mb-4">Responsible Personnel</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-4">
              <div>
                <label className="text-xs font-medium text-ink-muted">Responsible User</label>
                <p className="text-sm text-ink mt-1">{getPersonnelName(unit.responsibleUserId)}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-ink-muted">Designation</label>
                <p className="text-sm text-ink mt-1">{unit.responsiblePersonTitle || '-'}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-ink-muted">Proxy User</label>
                <p className="text-sm text-ink mt-1">{getPersonnelName(unit.proxyUserId)}</p>
              </div>
            </div>
          </div> */}

          {/* Embedded Posts Section */}
          {unit.posts && unit.posts.length > 0 && (
            <div className="ds-card lg:col-span-2 p-3">
              <div className="flex items-center justify-between mb-2 pb-2 border-b border-line">
                <h2 className="text-sm font-semibold text-ink">
                  Posts ({unit.posts.filter((p: any) => !p.isDelete).length})
                </h2>
                <div className="flex items-center gap-3 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    <span className="text-ink-muted">
                      {unit.posts.filter((p: any) => !p.isDelete && p.assignedUser).length} Assigned
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                    <span className="text-ink-muted">
                      {unit.posts.filter((p: any) => !p.isDelete && !p.assignedUser).length} Vacant
                    </span>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-line bg-gray-50 dark:bg-gray-700/30">
                      <th className="text-left py-1.5 px-2 font-medium text-ink-muted">Post</th>
                      <th className="text-left py-1.5 px-2 font-medium text-ink-muted">Personnel</th>
                      <th className="text-left py-1.5 px-2 font-medium text-ink-muted">Roles</th>
                      <th className="text-left py-1.5 px-2 font-medium text-ink-muted">Reports To</th>
                      <th className="text-center py-1.5 px-2 font-medium text-ink-muted">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unit.posts
                      .filter((post: any) => !post.isDelete)
                      .map((post: any) => (
                        <tr key={post.postCode} className="border-b border-line hover:bg-gray-50 dark:hover:bg-gray-700/30">
                          <td className="py-2 px-2">
                            <div className="flex flex-col">
                              <div className="flex items-center gap-1.5">
                                <span className="font-semibold text-ink">{post.postName}</span>
                                {post.isUnitHead && (
                                  <i className="pi pi-star-fill text-[9px] text-blue-600 dark:text-blue-400" title="Unit Head" />
                                )}
                              </div>
                              <span className="text-ink-muted font-mono">{post.postCode}</span>
                            </div>
                          </td>
                          <td className="py-2 px-2">
                            {post.assignedUser ? (
                              <span
                                onClick={() => {
                                  const userId = typeof post.assignedUser === 'string'
                                    ? post.assignedUser
                                    : post.assignedUser._id;
                                  navigate(`/personnel/${userId}`);
                                }}
                                className="inline-flex items-center gap-1.5 text-ink cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
                              >
                                <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center text-[10px] font-medium text-white flex-shrink-0">
                                  {typeof post.assignedUser === 'string'
                                    ? getPersonnelName(post.assignedUser)?.charAt(0) || '?'
                                    : (post.assignedUser.name || post.assignedUser.firstName || '?').charAt(0)}
                                </div>
                                <span className="font-medium">
                                  {typeof post.assignedUser === 'string'
                                    ? getPersonnelName(post.assignedUser)
                                    : (post.assignedUser.name || `${post.assignedUser.firstName} ${post.assignedUser.lastName}`)}
                                </span>
                              </span>
                            ) : (
                              <span className="text-ink-muted italic">Vacant</span>
                            )}
                          </td>
                          <td className="py-2 px-2">
                            {post.assignedRoles && post.assignedRoles.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {post.assignedRoles.map((role: any, idx: number) => {
                                  const displayName = typeof role === 'string'
                                    ? role
                                    : (role.roleName || role.name || role._id);
                                  const roleKey = typeof role === 'string' ? role : role._id;
                                  return (
                                    <span
                                      key={roleKey || idx}
                                      className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                                    >
                                      {displayName}
                                    </span>
                                  );
                                })}
                              </div>
                            ) : (
                              <span className="text-ink-muted">-</span>
                            )}
                          </td>
                          <td className="py-2 px-2">
                            {post.reportsToPost && (post.reportsToPost.postName || post.reportsToPost.unitName) ? (
                              <div className="flex flex-col">
                                <span className="text-ink">{post.reportsToPost.postName || 'N/A'}</span>
                                {post.reportsToPost.unitName && post.reportsToPost.unitId !== unit._id && (
                                  <span className="text-ink-muted text-[10px]">({post.reportsToPost.unitName})</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-ink-muted">-</span>
                            )}
                          </td>
                          <td className="py-2 px-2 text-center">
                            <div className="inline-flex items-center gap-1">
                              <div className={`w-1.5 h-1.5 rounded-full ${post.assignedUser ? 'bg-green-500' : 'bg-orange-500'}`}></div>
                              <span className={post.assignedUser ? 'text-green-700 dark:text-green-400' : 'text-orange-700 dark:text-orange-400'}>
                                {post.assignedUser ? 'Assigned' : 'Vacant'}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Unit Hierarchy */}
          {(() => {
            // parentUnitPath = ancestor _ids (root -> parent); append self for the full chain.
            const ancestorIds: string[] = Array.isArray((unit as any).parentUnitPath)
              ? (unit as any).parentUnitPath.filter(Boolean).map(String)
              : [];
            const parentPath: { _id?: string; name?: string }[] = [
              ...ancestorIds.map((aid) => ({ _id: aid })),
              { _id: (unit as any)._id || (unit as any).id, name: unit.name },
            ];
            // childrenUnitPath = ALL descendant _ids (any depth).
            const descendantIds: string[] = Array.isArray((unit as any).childrenUnitPath)
              ? (unit as any).childrenUnitPath.filter(Boolean).map(String)
              : [];
            const CAP = 60;
            const shownDescendants = descendantIds.slice(0, CAP);

            if (ancestorIds.length === 0 && descendantIds.length === 0) return null;

            return (
              <div className="ds-card lg:col-span-2 p-3">
                <h2 className="text-sm font-semibold text-ink mb-2">Unit Hierarchy</h2>

                <div className="text-xs mb-3">
                  <p className="text-ink-muted mb-1">Parent Path</p>
                  {parentPath.map((entry, index) => (
                    <div
                      key={entry._id || `${entry.name}-${index}`}
                      className="flex items-center py-1"
                      style={{ paddingLeft: `${index * 16}px` }}
                    >
                      <span className="text-gray-400 mr-2">
                        {index === parentPath.length - 1 ? '└' : '├'}
                      </span>
                      <span
                        className={
                          index === parentPath.length - 1
                            ? 'font-medium text-ink'
                            : 'text-ink-muted'
                        }
                      >
                        {labelFor(entry)}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="text-xs">
                  <p className="text-ink-muted mb-1">
                    Descendants{descendantIds.length > 0 ? ` (${descendantIds.length})` : ''}
                  </p>
                  {descendantIds.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {shownDescendants.map((did, index) => (
                        <span
                          key={did || index}
                          className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                        >
                          {labelFor({ _id: did })}
                        </span>
                      ))}
                      {descendantIds.length > CAP && (
                        <span className="inline-flex items-center px-1.5 py-0.5 text-[11px] text-ink-muted">
                          +{descendantIds.length - CAP} more
                        </span>
                      )}
                    </div>
                  ) : (
                    <p className="text-ink-muted">No descendants</p>
                  )}
                </div>
              </div>
            );
          })()}
        {/* Audit Information */}
          <AuditInfoCard
            createdAt={unit.createdAt}
            updatedAt={unit.updatedAt}
            createdBy={unit.createdBy}
            updatedBy={unit.updatedBy}
          />

          {/* Unit Personnel List - Commented out per request */}
          {/* {unit.unitPersonnelList && unit.unitPersonnelList.length > 0 && (
            <div className="ds-card p-5">
              <h2 className="text-base font-semibold text-ink mb-3">
                Unit Personnel ({unit.unitPersonnelList.length})
              </h2>
              <div className="flex flex-wrap gap-2">
                {unit.unitPersonnelList.map((userId: string) => (
                  <span
                    key={userId}
                    onClick={() => navigate(`/personnel/${userId}`)}
                    className="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    <i className="pi pi-user mr-2 text-xs" />
                    {getPersonnelName(userId)}
                  </span>
                ))}
              </div>
            </div>
          )} */}
         

          {/* Responsible Post History */}
          {unit.responsiblePostHistory && unit.responsiblePostHistory.length > 0 && (
            <div className="ds-card lg:col-span-2 p-3">
              <h2 className="text-sm font-semibold text-ink mb-2">
                Responsible Post History
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-line">
                      <th className="text-left py-1.5 px-2 font-medium text-ink-muted">Post Code</th>
                      <th className="text-left py-1.5 px-2 font-medium text-ink-muted">Title</th>
                      <th className="text-left py-1.5 px-2 font-medium text-ink-muted">Period</th>
                      <th className="text-left py-1.5 px-2 font-medium text-ink-muted">Reason</th>
                      <th className="text-left py-1.5 px-2 font-medium text-ink-muted">Changed By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unit.responsiblePostHistory?.map((history: any, index: number) => (
                      <tr
                        key={index}
                        className="border-b border-line last:border-b-0"
                      >
                        <td className="py-1.5 px-2 text-ink">
                          <span className="font-mono">{history.postCode || '-'}</span>
                          {index === (unit.responsiblePostHistory?.length ?? 0) - 1 && (
                            <Tag value="Previous" severity="warning" className="ml-1.5 text-xs px-1.5 py-0.5" />
                          )}
                        </td>
                        <td className="py-1.5 px-2 text-ink-muted">{history.title || '-'}</td>
                        <td className="py-1.5 px-2 text-ink-muted">
                          {formatDate(history.from)} - {formatDate(history.to)}
                        </td>
                        <td className="py-1.5 px-2 text-ink-muted">{history.reason || '-'}</td>
                        <td className="py-1.5 px-2 text-ink-muted">
                          {getPersonnelName(history.changedBy)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
      </div>

      {/* Delete Dialog */}
      <Dialog
        visible={deleteDialogOpen}
        onHide={() => setDeleteDialogOpen(false)}
        header="Delete Unit"
        footer={deleteDialogFooter}
        style={{ width: '400px' }}
      >
        <p className="text-sm text-ink-muted">
          Are you sure you want to delete <strong>{unit.name}</strong>? This action will mark the unit as deleted.
        </p>
      </Dialog>

      {/* Restore Dialog */}
      <Dialog
        visible={restoreDialogOpen}
        onHide={() => setRestoreDialogOpen(false)}
        header="Restore Unit"
        footer={restoreDialogFooter}
        style={{ width: '400px' }}
      >
        <p className="text-sm text-ink-muted">
          Are you sure you want to restore <strong>{unit.name}</strong>? This will make the unit active again.
        </p>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        visible={imagePreviewOpen}
        onHide={() => setImagePreviewOpen(false)}
        header={unit.name}
        style={{ width: 'auto', maxWidth: '90vw' }}
        dismissableMask
        className="image-preview-dialog"
      >
        {logoUrl && (
          <div className="flex justify-center">
            <img
              src={logoUrl}
              alt={unit.name}
              className="max-w-full max-h-[70vh] object-contain"
            />
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default UnitView;
